'use client';

/**
 * /crm — leads + mail cockpit over the sites-hub /api/v1 (Phase 5.C).
 * Beast is the cockpit, the hub stays the engine: leads and Gmail threads are
 * read live from the hub; replying happens in the hub UI (deep link) until
 * v1 grows a send endpoint.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Users, Loader2, ChevronDown, Search, Mail, Inbox, ExternalLink,
  Phone, MapPin, Clock, X, MessageSquare, Paperclip,
} from 'lucide-react';

interface HubSite { slug: string; name: string; domain: string; crmEnabled: boolean }
interface Lead {
  id: string; created_at: string; name: string; email: string; phone: string;
  message: string; city: string; service: string | null; page_url: string; status: string | null;
  site?: string; siteDomain?: string;
}
interface Thread {
  threadId: string; subject: string; fromName: string; from: string; date: string;
  snippet: string; unread: boolean; messageCount: number; hasAttachments: boolean;
  site?: string; siteDomain?: string;
}

const BOXES = [
  { id: 'inbox', label: 'Incoming' },
  { id: 'sent', label: 'Sent' },
  { id: 'all', label: 'All mail' },
] as const;

const PERIODS = [
  { id: '7', label: '7d' },
  { id: '30', label: '30d' },
  { id: '', label: 'All time' },
] as const;

const HUB_URL = 'https://app.anydomain.ca';

function timeAgo(iso: string): string {
  // hub timestamps look like '2026-06-27 22:09:31.733+00' — normalize the
  // space and the bare '+00' offset or Date() returns NaN in the browser.
  const d = new Date(iso.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'));
  const s = (Date.now() - d.getTime()) / 1000;
  if (isNaN(s)) return iso;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-500/15 text-blue-300',
  contacted: 'bg-amber-500/15 text-amber-300',
  won: 'bg-green-500/15 text-green-300',
  lost: 'bg-gray-500/15 text-gray-400',
};

export default function CrmPage({ scopeDomains, scopeName }: { scopeDomains?: string[]; scopeName?: string } = {}) {
  // HARD scope: only this project's CRM sites (Boss 2026-07-29: charters CRM showed all projects)
  const normDom = (s: string) => String(s || '').toLowerCase().replace(/^www\./, '');
  const scopeSet = scopeDomains?.map(normDom);
  const [sites, setSites] = useState<HubSite[]>([]);
  const [site, setSite] = useState('');
  const [tab, setTab] = useState<'leads' | 'mail'>('leads');

  // leads state
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<{ lead: Lead; timeline: any[] } | null>(null);

  // mail state
  const [threads, setThreads] = useState<Thread[]>([]);
  const [mailbox, setMailbox] = useState<string>('');
  const [box, setBox] = useState<string>('inbox');
  const [days, setDays] = useState<string>('30');
  const [mailLoading, setMailLoading] = useState(false);
  const [openThread, setOpenThread] = useState<any>(null);
  const [threadLoading, setThreadLoading] = useState(false);

  useEffect(() => {
    fetch('/api/hub/sites')
      .then(r => r.json())
      .then(d => {
        let crm = (d.sites || []).filter((s: HubSite) => s.crmEnabled);
        if (scopeSet) crm = crm.filter((s: HubSite) => scopeSet.includes(normDom(s.domain)));
        setSites(crm);
        if (crm.length) setSite(crm.length === 1 ? crm[0].slug : 'all');
      })
      .catch(e => toast.error(e.message));
  }, []);

  const loadLeads = useCallback((s: string, query: string) => {
    if (!s) return;
    setLoading(true);
    const sp = new URLSearchParams({ limit: '100' });
    if (query) sp.set('q', query);
    fetch(`/api/hub/crm/${s}/leads?${sp}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        let ls = d.leads || [];
        // 'all' endpoint spans every hub site — clamp to project scope
        if (s === 'all' && scopeSet) ls = ls.filter((l: Lead) => scopeSet.includes(normDom(l.siteDomain || l.site || '')));
        setLeads(ls);
        setTotal(s === 'all' && scopeSet ? ls.length : (d.total || 0));
      })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeDomains?.join(',')]);

  const loadMail = useCallback((s: string, b: string, d: string) => {
    if (!s) return;
    setMailLoading(true);
    const sp = new URLSearchParams();
    if (b && b !== 'inbox') sp.set('box', b);
    if (d) sp.set('q', `newer_than:${d}d`);
    fetch(`/api/hub/crm/${s}/mail${sp.toString() ? `?${sp}` : ''}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        setThreads(d.threads || []);
        setMailbox(d.mailbox?.address || '');
      })
      .catch(e => toast.error(e.message))
      .finally(() => setMailLoading(false));
  }, []);

  useEffect(() => {
    setSelected(null); setOpenThread(null);
    if (tab === 'leads') loadLeads(site, q);
    else loadMail(site, box, days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, tab, box, days]);

  // Always fresh (Boss 2026-07-07: "it has to update all the time") —
  // refetch every 2 min + whenever the window regains focus.
  useEffect(() => {
    const refresh = () => { if (tab === 'leads') loadLeads(site, q); else loadMail(site, box, days); };
    const iv = setInterval(refresh, 120_000);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(iv); window.removeEventListener('focus', refresh); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, tab, box, days, q]);

  const openLead = (l: Lead) => {
    setSelected({ lead: l, timeline: [] });
    fetch(`/api/hub/crm/${l.site || site}/leads/${l.id}`)
      .then(r => r.json())
      .then(d => { if (!d.error) setSelected({ lead: { ...l, ...(d.lead || {}) }, timeline: d.timeline || [] }); })
      .catch(() => {});
  };

  const viewThread = (t: Thread) => {
    setThreadLoading(true);
    setOpenThread({ meta: t, messages: [] });
    const threadSite = t.site || (site === 'all' ? sites[0]?.slug : site);
    fetch(`/api/hub/crm/${threadSite}/mail?thread=${t.threadId}`)
      .then(r => r.json())
      .then(d => { if (!d.error) setOpenThread({ meta: t, messages: d.thread?.messages || d.messages || [] }); })
      .catch(e => toast.error(e.message))
      .finally(() => setThreadLoading(false));
  };

  const activeSite = sites.find(s => s.slug === site);
  const scopeLabel = site === 'all' ? (scopeName ? `All ${scopeName} sites` : 'All projects') : activeSite?.domain;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
            <Users className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">CRM</h1>
            <p className="text-xs text-gray-500">Leads & mail across all hub sites — Beast is the cockpit, the hub is the engine.</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={site} onChange={e => setSite(e.target.value)}
              className="appearance-none bg-[#0d1117]/85 border border-white/[0.08] rounded-lg pl-3 pr-9 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer max-w-[220px]"
            >
              <option value="all">{scopeName ? `All ${scopeName} sites` : 'All projects'} ({sites.length})</option>
              {sites.map(s => <option key={s.slug} value={s.slug}>{s.domain}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
          <div className="flex rounded-lg border border-white/[0.08] overflow-hidden">
            <button onClick={() => setTab('leads')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs transition-colors ${tab === 'leads' ? 'bg-cyan-500/15 text-cyan-300' : 'bg-[#0d1117]/85 text-gray-400 hover:text-white'}`}>
              <Users className="w-3.5 h-3.5" /> Leads
            </button>
            <button onClick={() => setTab('mail')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs transition-colors ${tab === 'mail' ? 'bg-cyan-500/15 text-cyan-300' : 'bg-[#0d1117]/85 text-gray-400 hover:text-white'}`}>
              <Mail className="w-3.5 h-3.5" /> Mail
            </button>
          </div>
          <a href={`${HUB_URL}/crm`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.05] text-xs text-gray-400 hover:text-white hover:border-cyan-500/40 transition-colors">
            <ExternalLink className="w-3.5 h-3.5" /> Open in Hub
          </a>
        </div>
      </div>

      {/* ============ LEADS ============ */}
      {tab === 'leads' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
              <input
                value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadLeads(site, q)}
                placeholder="Search name, email, message… (Enter)"
                className="w-full bg-[#0d1117]/85 border border-white/[0.08] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/50"
              />
            </div>
            <span className="text-xs text-gray-500">{total} lead{total === 1 ? '' : 's'} · {scopeLabel}</span>
          </div>

          {/* Mobile: detail panel stacks under the table; ≥lg keeps the two-column split */}
          <div className={`grid gap-4 grid-cols-1 ${selected ? 'lg:grid-cols-[1fr_380px]' : ''}`}>
            <div className="rounded-xl border border-white/[0.08] overflow-hidden overflow-x-auto">
              {loading ? (
                <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
              ) : leads.length === 0 ? (
                <div className="text-center py-16 text-gray-500 text-sm">No leads{q ? ' matching search' : ''}.</div>
              ) : (
                <table className="w-full min-w-[560px] text-xs">
                  <thead>
                    <tr className="text-gray-500 text-left border-b border-white/[0.04] bg-white/[0.02]">
                      <th className="py-2.5 px-3 font-medium">Lead</th>
                      <th className="py-2.5 px-3 font-medium">Message</th>
                      <th className="py-2.5 px-3 font-medium">City</th>
                      <th className="py-2.5 px-3 font-medium text-right">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map(l => (
                      <tr key={`${l.site || site}-${l.id}`} onClick={() => openLead(l)}
                        className={`border-b border-white/[0.04] cursor-pointer transition-colors ${selected?.lead.id === l.id && selected?.lead.site === l.site ? 'bg-cyan-500/10' : 'hover:bg-white/5'}`}>
                        <td className="py-2.5 px-3">
                          <p className="text-white font-medium">{l.name || '—'}</p>
                          <p className="text-gray-500">{l.email}</p>
                          {site === 'all' && l.siteDomain && <span className="inline-block mt-0.5 mr-1 text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">{l.siteDomain}</span>}
                          {l.status && <span className={`inline-block mt-0.5 text-[9px] uppercase px-1.5 py-0.5 rounded ${STATUS_COLORS[l.status] || 'bg-gray-500/15 text-gray-400'}`}>{l.status}</span>}
                        </td>
                        <td className="py-2.5 px-3 text-gray-400 max-w-md"><p className="line-clamp-2">{l.message || '—'}</p></td>
                        <td className="py-2.5 px-3 text-gray-400">{l.city || '—'}</td>
                        <td className="py-2.5 px-3 text-gray-500 text-right whitespace-nowrap">{timeAgo(l.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Lead detail */}
            {selected && (
              <div className="rounded-xl border border-cyan-500/25 bg-white/[0.04] p-4 h-fit sticky top-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-white">{selected.lead.name || 'Unnamed lead'}</h3>
                    <p className="text-[11px] text-gray-500">#{selected.lead.id} · {timeAgo(selected.lead.created_at)}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="space-y-2 text-xs">
                  {selected.lead.email && (
                    <a href={`mailto:${selected.lead.email}`} className="flex items-center gap-2 text-cyan-300 hover:text-cyan-200">
                      <Mail className="w-3.5 h-3.5" /> {selected.lead.email}
                    </a>
                  )}
                  {selected.lead.phone && (
                    <a href={`tel:${selected.lead.phone}`} className="flex items-center gap-2 text-cyan-300 hover:text-cyan-200">
                      <Phone className="w-3.5 h-3.5" /> {selected.lead.phone}
                    </a>
                  )}
                  {selected.lead.city && (
                    <p className="flex items-center gap-2 text-gray-400"><MapPin className="w-3.5 h-3.5" /> {selected.lead.city}</p>
                  )}
                  {selected.lead.page_url && (
                    <p className="flex items-center gap-2 text-gray-500"><ExternalLink className="w-3.5 h-3.5" /> from {selected.lead.page_url}</p>
                  )}
                  {selected.lead.message && (
                    <div className="rounded-lg bg-white/5 p-3 text-gray-300 whitespace-pre-wrap leading-relaxed flex gap-2">
                      <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-500" />
                      <span>{selected.lead.message}</span>
                    </div>
                  )}
                  {selected.timeline.length > 0 && (
                    <div className="pt-2 border-t border-white/[0.04]">
                      <p className="text-[10px] uppercase text-gray-500 mb-2">Timeline</p>
                      <div className="space-y-1.5">
                        {selected.timeline.map((ev: any, i: number) => (
                          <p key={i} className="flex items-center gap-2 text-gray-400">
                            <Clock className="w-3 h-3 text-gray-600" />
                            {ev.type || ev.event || 'event'} · {timeAgo(ev.created_at || ev.date || '')}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  <a href={`${HUB_URL}/crm/${selected.lead.site || site}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 mt-3 px-3 py-2 rounded-lg bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" /> Manage in Hub
                  </a>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ============ MAIL ============ */}
      {tab === 'mail' && (
        <div className="grid" style={{ gridTemplateColumns: openThread ? '1fr 1.2fr' : '1fr', gap: '1rem' }}>
          <div className="rounded-xl border border-white/[0.08] overflow-hidden h-fit">
            <div className="px-3 py-2 border-b border-white/[0.04] bg-white/[0.02] flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs text-gray-400 flex items-center gap-1.5"><Inbox className="w-3.5 h-3.5" /> {site === 'all' ? 'All projects' : (mailbox || activeSite?.domain)}</span>
              <div className="flex items-center gap-2">
                <div className="flex rounded-md border border-white/[0.08] overflow-hidden">
                  {BOXES.map(b => (
                    <button key={b.id} onClick={() => setBox(b.id)}
                      className={`px-2 py-1 text-[10px] transition-colors ${box === b.id ? 'bg-cyan-500/15 text-cyan-300' : 'text-gray-500 hover:text-white'}`}>
                      {b.label}
                    </button>
                  ))}
                </div>
                <div className="flex rounded-md border border-white/[0.08] overflow-hidden">
                  {PERIODS.map(p => (
                    <button key={p.id} onClick={() => setDays(p.id)}
                      className={`px-2 py-1 text-[10px] transition-colors ${days === p.id ? 'bg-cyan-500/15 text-cyan-300' : 'text-gray-500 hover:text-white'}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
                <span className="text-[10px] text-gray-600">{threads.length} threads</span>
              </div>
            </div>
            {mailLoading ? (
              <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
            ) : threads.length === 0 ? (
              <div className="text-center py-16 text-gray-500 text-sm">Inbox empty.</div>
            ) : (
              <div>
                {threads.map(t => (
                  <button key={t.threadId} onClick={() => viewThread(t)}
                    className={`w-full text-left px-3 py-2.5 border-b border-white/[0.04] transition-colors ${openThread?.meta?.threadId === t.threadId ? 'bg-cyan-500/10' : 'hover:bg-white/5'}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${t.unread ? 'font-bold text-white' : 'text-gray-300'}`}>{t.fromName || t.from}</span>
                      {site === 'all' && t.siteDomain && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">{t.siteDomain}</span>
                      )}
                      {t.hasAttachments && <Paperclip className="w-3 h-3 text-gray-600" />}
                      {t.messageCount > 1 && <span className="text-[10px] text-gray-600">×{t.messageCount}</span>}
                      <span className="flex-1" />
                      <span className="text-[10px] text-gray-600 whitespace-nowrap">{t.date?.replace(/\s[+-]\d{4}$/, '').slice(0, 22)}</span>
                    </div>
                    <p className={`text-xs mt-0.5 ${t.unread ? 'text-gray-200 font-medium' : 'text-gray-400'}`}>{t.subject || '(no subject)'}</p>
                    <p className="text-[11px] text-gray-600 line-clamp-1 mt-0.5">{t.snippet}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Thread reader */}
          {openThread && (
            <div className="rounded-xl border border-cyan-500/25 bg-white/[0.04] p-4 h-fit sticky top-4">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-semibold text-white pr-4">{openThread.meta?.subject || '(no subject)'}</h3>
                <button onClick={() => setOpenThread(null)} className="text-gray-500 hover:text-white flex-shrink-0"><X className="w-4 h-4" /></button>
              </div>
              {threadLoading ? (
                <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>
              ) : (
                <div className="space-y-3 max-h-[560px] overflow-y-auto">
                  {(openThread.messages || []).map((m: any, i: number) => (
                    <div key={i} className="rounded-lg bg-white/5 p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-xs font-medium ${m.outgoing ? 'text-green-300' : 'text-cyan-300'}`}>{m.outgoing ? '→ ' : ''}{m.fromName || m.from}</span>
                        <span className="text-[10px] text-gray-600">{(m.date || '').replace(/\s[+-]\d{4}$/, '').slice(0, 22)}</span>
                      </div>
                      <p className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed">
                        {(m.bodyText || m.textPlain || m.body || m.snippet || '').slice(0, 4000)}
                      </p>
                      {(m.attachments || []).length > 0 && (
                        <p className="text-[10px] text-gray-500 mt-1.5 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {m.attachments.length} attachment{m.attachments.length === 1 ? '' : 's'}</p>
                      )}
                    </div>
                  ))}
                  {(openThread.messages || []).length === 0 && !threadLoading && (
                    <p className="text-xs text-gray-500">{openThread.meta?.snippet}</p>
                  )}
                </div>
              )}
              <a href={`${HUB_URL}/crm/${openThread.meta?.site || site}/mail`} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 mt-3 px-3 py-2 rounded-lg bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 text-xs transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Reply in Hub
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
